// Master catalog of all sidebar modules — single source of truth for routing,
// feature gating and sidebar rendering.
//
// Each entry maps a backend MODULE id (or a sidebar-only logical id) to its
// presentation metadata. The sidebar reads this catalog + the tenant plan to
// decide what to show:
//   - included module          → render normal NavLink
//   - module available in a higher plan → render with UPGRADE badge, link to LockedModulePage
//   - enterprise-only module   → render with ENTERPRISE badge, link to LockedModulePage
//
// Categories map to the sidebar section headers. Items inside each category
// preserve declaration order.
import {
  LayoutDashboard,
  Users,
  CreditCard,
  FileText,
  Activity,
  Settings,
  Utensils,
  PlusCircle,
  ListOrdered,
  ChefHat,
  CookingPot,
  BookOpen,
  CalendarDays,
  ShoppingBag,
  UserSquare2,
  Wallet,
  Warehouse,
  Truck,
  Carrot,
  ArrowLeftRight,
  Brain,
  LineChart,
  Sparkles,
  TrendingDown,
  Crosshair,
  PieChart,
  BarChart3,
  Bell,
  Scale,
} from "lucide-react";

// Categories rendered in this order (top → bottom in the sidebar).
export const CATEGORY_ORDER = [
  "operative",
  "restaurant",
  "supply",
  "ai_controller",
  "intelligence",
  "growth",
];

export const CATEGORY_LABELS = {
  operative: "AREA OPERATIVA",
  restaurant: "RISTORANTE",
  supply: "MAGAZZINO & COSTI",
  ai_controller: "AI CONTROLLER",
  intelligence: "BUSINESS INTELLIGENCE",
  growth: "CRESCITA",
};

// Modules catalog.
//   requiredModule:  the backend MODULES id needed for gating. Some sidebar
//                    items (Dashboard, Team, Billing…) are always available
//                    to authenticated users and have `requiredModule: null`.
//   route:           absolute frontend route.
//   category:        sidebar section id.
//   visibleInSidebar: false = hidden from sidebar but still routable.
//   mobileEnabled:   false = hidden on viewport <640 (advanced features).
export const MODULES_CATALOG = {
  // ───── AREA OPERATIVA (always visible) ─────
  dashboard: {
    id: "dashboard",
    label: "Dashboard",
    description: "Panoramica operativa, KPI giornalieri, alert.",
    icon: LayoutDashboard,
    route: "/dashboard",
    category: "operative",
    requiredModule: null,
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  team: {
    id: "team",
    label: "Team & Utenti",
    description: "Gestione utenti, ruoli, permessi.",
    icon: Users,
    route: "/team",
    category: "operative",
    requiredModule: null,
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  billing: {
    id: "billing",
    label: "Abbonamento",
    description: "Gestione piano, fatturazione, fatture.",
    icon: CreditCard,
    route: "/billing",
    category: "operative",
    requiredModule: null,
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  documents: {
    id: "documents",
    label: "Documenti legali",
    description: "Contratti, accettazioni GDPR, ricevute fiscali.",
    icon: FileText,
    route: "/legal/contracts",
    category: "operative",
    requiredModule: null,
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  activity_log: {
    id: "activity_log",
    label: "Log attività",
    description: "Audit log accessi, modifiche, eventi di sistema.",
    icon: Activity,
    route: "/activity-log",
    category: "operative",
    requiredModule: null,
    visibleInSidebar: false, // hidden by default — owner/admin sezione avanzata
    mobileEnabled: false,
  },
  settings: {
    id: "settings",
    label: "Impostazioni",
    description: "Profilo struttura, lingua, fuso, branding.",
    icon: Settings,
    route: "/settings",
    category: "operative",
    requiredModule: null,
    visibleInSidebar: true,
    mobileEnabled: true,
  },

  // ───── RISTORANTE ─────
  restaurant_overview: {
    id: "restaurant_overview",
    label: "Panoramica ristorante",
    description: "Vista d'insieme operatività ristorante.",
    icon: Utensils,
    route: "/restaurant",
    category: "restaurant",
    requiredModule: "restaurant_core",
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  new_order: {
    id: "new_order",
    label: "Nuovo ordine",
    description: "Apri nuova comanda in pochi tocchi.",
    icon: PlusCircle,
    route: "/restaurant/orders/new",
    category: "restaurant",
    requiredModule: "pos",
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  orders: {
    id: "orders",
    label: "Ordini",
    description: "Lista comande, stato, dettaglio.",
    icon: ListOrdered,
    route: "/restaurant/orders",
    category: "restaurant",
    requiredModule: "pos",
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  kitchen: {
    id: "kitchen",
    label: "Cucina (KDS)",
    description: "Kitchen Display System, ticket cucina.",
    icon: ChefHat,
    route: "/restaurant/kitchen",
    category: "restaurant",
    requiredModule: "pos",
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  tables: {
    id: "tables",
    label: "Sale e Tavoli",
    description: "Stato tavoli, sale, capienze, gestione operativa.",
    icon: CookingPot,
    route: "/restaurant/tables",
    category: "restaurant",
    requiredModule: "pos",
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  menu: {
    id: "menu",
    label: "Menu",
    description: "Voci di menu, categorie, varianti, prezzi.",
    icon: BookOpen,
    route: "/restaurant/menu",
    category: "restaurant",
    requiredModule: "pos",
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  reservations: {
    id: "reservations",
    label: "Prenotazioni",
    description: "Calendario, conferme, no-show, customer DB.",
    icon: CalendarDays,
    route: "/restaurant/reservations",
    category: "restaurant",
    requiredModule: "reservations",
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  online_reservations: {
    id: "online_reservations",
    label: "Prenotazioni online",
    description: "Link pubblico per la prenotazione diretta dei clienti finali.",
    icon: CalendarDays,
    route: "/restaurant/reservations?source=online",
    category: "restaurant",
    requiredModule: "online_reservations",
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  takeaway: {
    id: "takeaway",
    label: "Asporto",
    description: "Ordini per ritiro, slot orari, status flow.",
    icon: ShoppingBag,
    route: "/restaurant/takeaway",
    category: "restaurant",
    requiredModule: "takeaway",
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  customers: {
    id: "customers",
    label: "Clienti",
    description: "Anagrafica clienti diretti + storico ordini.",
    icon: UserSquare2,
    route: "/restaurant/customers",
    category: "restaurant",
    requiredModule: "pos",
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  payments: {
    id: "payments",
    label: "Pagamenti",
    description: "Incassi, metodi, riconciliazione.",
    icon: Wallet,
    route: "/restaurant/payments",
    category: "restaurant",
    requiredModule: "payments",
    visibleInSidebar: true,
    mobileEnabled: true,
  },

  // ───── MAGAZZINO & COSTI ─────
  inventory: {
    id: "inventory",
    label: "Magazzino",
    description: "Stock real-time, alert sotto soglia.",
    icon: Warehouse,
    route: "/restaurant/inventory",
    category: "supply",
    requiredModule: "inventory",
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  suppliers: {
    id: "suppliers",
    label: "Fornitori",
    description: "Anagrafica fornitori, listini, ordini.",
    icon: Truck,
    route: "/restaurant/suppliers",
    category: "supply",
    requiredModule: "inventory",
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  ingredients: {
    id: "ingredients",
    label: "Ingredienti",
    description: "Catalogo, costi medio ponderato, rese.",
    icon: Carrot,
    route: "/restaurant/ingredients",
    category: "supply",
    requiredModule: "inventory",
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  movements: {
    id: "movements",
    label: "Movimenti",
    description: "Carichi, scarichi, sprechi, rettifiche.",
    icon: ArrowLeftRight,
    route: "/restaurant/movements",
    category: "supply",
    requiredModule: "inventory",
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  recipes: {
    id: "recipes",
    label: "Ricette",
    description: "Distinte base, costo porzione, scaling.",
    icon: BookOpen,
    route: "/restaurant/recipes",
    category: "supply",
    requiredModule: "recipes",
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  food_cost: {
    id: "food_cost",
    label: "Food Cost",
    description: "Food cost reale vs teorico, alert anomalie.",
    icon: Scale,
    route: "/restaurant/food-cost",
    category: "supply",
    requiredModule: "food_cost",
    visibleInSidebar: true,
    mobileEnabled: true,
  },

  // ───── AI CONTROLLER ─────
  ai_controller: {
    id: "ai_controller",
    label: "Controller finanziario",
    description: "AI Financial Controller: alert e analisi senior.",
    icon: Brain,
    route: "/ai-controller",
    category: "ai_controller",
    requiredModule: "ai_controller",
    visibleInSidebar: true,
    mobileEnabled: true,
  },
  profit_recovery: {
    id: "profit_recovery",
    label: "Profit Recovery",
    description: "Decision layer, recovered money tracker, what-if.",
    icon: Wallet,
    route: "/ai-controller/profit-recovery",
    category: "ai_controller",
    requiredModule: "profit_recovery",
    visibleInSidebar: true,
    mobileEnabled: true,
  },

  // ───── BUSINESS INTELLIGENCE ─────
  waste_control: {
    id: "waste_control",
    label: "Controllo sprechi",
    description: "Tracking e riduzione sprechi cucina.",
    icon: TrendingDown,
    route: "/modules/waste_control",
    category: "intelligence",
    requiredModule: "waste_control",
    visibleInSidebar: true,
    mobileEnabled: true,
    placeholder: true, // route to PlaceholderModule when accessed
  },
  margin_analysis: {
    id: "margin_analysis",
    label: "Analisi marginalità",
    description: "Margine per piatto / categoria / fascia.",
    icon: PieChart,
    route: "/modules/margin_analysis",
    category: "intelligence",
    requiredModule: "margin_analysis",
    visibleInSidebar: true,
    mobileEnabled: true,
    placeholder: true,
  },
  break_even: {
    id: "break_even",
    label: "Punto di pareggio",
    description: "Break-even dinamico + simulazione coperti.",
    icon: Crosshair,
    route: "/modules/break_even",
    category: "intelligence",
    requiredModule: "break_even",
    visibleInSidebar: true,
    mobileEnabled: true,
    placeholder: true,
  },
  business_analytics: {
    id: "business_analytics",
    label: "Business Analytics",
    description: "Analytics avanzata multi-sede e predictive.",
    icon: BarChart3,
    route: "/modules/business_analytics",
    category: "intelligence",
    requiredModule: "business_analytics",
    visibleInSidebar: true,
    mobileEnabled: true,
    placeholder: true,
    enterpriseOnly: true,
  },

  // ───── ENTERPRISE-ONLY (Phase 12.1 Plan Gating Hardening) ─────
  // Mirror of plans_catalog.ENTERPRISE_ONLY_MODULES. Combo Premium must
  // NEVER see these in the sidebar nor be able to access their routes.
  multi_location_dashboard: {
    id: "multi_location_dashboard",
    label: "Dashboard di Gruppo",
    description: "Dashboard consolidata multi-sede + gestione locations.",
    icon: BarChart3,
    route: "/enterprise/dashboard",
    category: "intelligence",
    requiredModule: "multi_location_dashboard",
    visibleInSidebar: false,
    mobileEnabled: true,
    enterpriseOnly: true,
  },
  benchmark: {
    id: "benchmark",
    label: "Benchmark Sedi",
    description: "Confronto KPI fra sedi del gruppo.",
    icon: BarChart3,
    route: "/enterprise/benchmark",
    category: "intelligence",
    requiredModule: "benchmark",
    visibleInSidebar: false,
    mobileEnabled: true,
    enterpriseOnly: true,
  },
  audit_trail_enterprise: {
    id: "audit_trail_enterprise",
    label: "Audit Trail Enterprise",
    description: "Audit trail unificato + Custom Roles + Permission Inspector.",
    icon: Activity,
    route: "/audit-trail",
    category: "intelligence",
    requiredModule: "audit_trail_enterprise",
    visibleInSidebar: false,
    mobileEnabled: true,
    enterpriseOnly: true,
  },
  executive_financial: {
    id: "executive_financial",
    label: "Executive Financial",
    description: "Financial Planning, P&L, periodi, exec dashboard.",
    icon: BarChart3,
    route: "/finance/executive-financial",
    category: "intelligence",
    requiredModule: "executive_financial",
    visibleInSidebar: false,
    mobileEnabled: true,
    enterpriseOnly: true,
  },
  intelligence_bi: {
    id: "intelligence_bi",
    label: "Business Intelligence",
    description: "BI Dashboard, Operations Center 2.0, Executive Alerts, Data Activation.",
    icon: BarChart3,
    route: "/bi/dashboard",
    category: "intelligence",
    requiredModule: "intelligence_bi",
    visibleInSidebar: false,
    mobileEnabled: true,
    enterpriseOnly: true,
  },

  // ───── CRESCITA ─────
  reports: {
    id: "reports",
    label: "Report",
    description: "Report PDF/CSV operativi e direzionali.",
    icon: LineChart,
    route: "/modules/reports",
    category: "growth",
    requiredModule: "reports",
    visibleInSidebar: true,
    mobileEnabled: true,
    placeholder: true,
  },
  notifications: {
    id: "notifications",
    label: "Notifiche",
    description: "Centro notifiche multi-canale.",
    icon: Bell,
    route: "/modules/notifications",
    category: "growth",
    requiredModule: "notifications",
    visibleInSidebar: true,
    mobileEnabled: true,
    placeholder: true,
  },
};

export const MODULES_LIST = Object.values(MODULES_CATALOG);

// Reverse lookup: backend module id → list of sidebar items that depend on it.
export const SIDEBAR_BY_REQUIRED_MODULE = MODULES_LIST.reduce((acc, m) => {
  if (m.requiredModule) {
    (acc[m.requiredModule] = acc[m.requiredModule] || []).push(m.id);
  }
  return acc;
}, {});

export function getModule(moduleId) {
  return MODULES_CATALOG[moduleId] || null;
}

export function getModulesByCategory(categoryId) {
  return MODULES_LIST.filter((m) => m.category === categoryId);
}
